import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import SearchDropdown from '../../applicationUi/components/SearchDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { updateClientJourneyProgress, fetchClientJourneyById } from '../../store/thunks/clientJourneyThunk';
import { XMarkIcon } from '@heroicons/react/24/outline';

const barHeight = 16;
const markerSize = 20;

const loopIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }}>
    <path d="M4 8a4 4 0 0 1 4-4h2" stroke="#4f46e5" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M12 8a4 4 0 0 1-4 4h-2" stroke="#4f46e5" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M10 4l1 1-1 1" stroke="#4f46e5" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M6 12l-1-1 1-1" stroke="#4f46e5" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  </svg>
);

const redXIcon = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4L4 10" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4 4L10 10" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ClientJourneyProgressBar = ({ 
  clientId,
  onUpdateProgress = () => {}
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { currentJourney } = useSelector((state) => state.clientJourney);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [isStepComplete, setIsStepComplete] = useState(false);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [stepNotes, setStepNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);

  if (!currentJourney?.journeyTemplateId || !clientId) {
    return null;
  }

  // Ensure all sections have valid steps
  const validSections = currentJourney.journeyTemplateId.sections.map(section => ({
    ...section,
    substeps: (section.steps || []).filter(step => step && typeof step === 'object' && step.name)
  }));

  const allSteps = validSections.flatMap(s => s.substeps);

  // Calculate the furthest completed step
  const calculateFurthestStep = () => {
    if (!currentJourney.completedSteps || currentJourney.completedSteps.length === 0) return 0;

    let furthestStep = 0;
    let currentCount = 0;

    for (let i = 0; i < validSections.length; i++) {
      const section = validSections[i];
      const sectionSteps = section.substeps.length;
      
      // Check if any steps in this section are completed
      const sectionCompletedSteps = currentJourney.completedSteps.filter(
        step => step.sectionIndex === i
      );

      if (sectionCompletedSteps.length > 0) {
        // Find the highest step index in this section
        const highestStepInSection = Math.max(
          ...sectionCompletedSteps.map(step => step.stepIndex)
        );
        furthestStep = currentCount + highestStepInSection;
      }

      currentCount += sectionSteps;
    }

    return furthestStep;
  };

  // Use the furthest completed step as the current step
  let stepIdx = calculateFurthestStep();
  
  // If looping, visually reset tracker to Therapy Block In Progress (if present)
  const therapyBlockIdx = allSteps.findIndex(s => s?.name?.toLowerCase().includes('therapy block'));
  const recommendationIdx = allSteps.findIndex(s => s?.name?.toLowerCase().includes('recommendation'));
  const showLoop = currentJourney.loopCount > 1 && stepIdx === recommendationIdx && therapyBlockIdx !== -1;
  if (showLoop) stepIdx = therapyBlockIdx;

  // Calculate progress percentage to be halfway between current and next step
  const progressPercentage = ((stepIdx + 0.5) / (allSteps.length - 1)) * 100;

  // Helper function to check if a step is completed
  const isStepCompleted = (stepIndex, sectionIndex) => {
    return currentJourney.completedSteps.some(
      step => step.stepIndex === stepIndex && step.sectionIndex === sectionIndex
    );
  };

  const handleStepClick = (idx, step) => {
    // Find the section and step indices
    let sectionIdx = 0;
    let stepIdx = 0;
    let currentCount = 0;

    for (let i = 0; i < validSections.length; i++) {
      const section = validSections[i];
      if (currentCount + section.substeps.length > idx) {
        sectionIdx = i;
        stepIdx = idx - currentCount;
        break;
      }
      currentCount += section.substeps.length;
    }

    setSelectedSection(sectionIdx);
    setSelectedStep(stepIdx);
    setIsStepComplete(isStepCompleted(stepIdx, sectionIdx));
    setIsModalOpen(true);
  };

  const handleUpdateProgress = async () => {
    if (!clientId) {
      console.error('[ClientJourneyProgressBar] No clientId provided');
      toast.error('Unable to update progress: Client ID missing');
      return;
    }

    if (!user?._id) {
      console.error('[ClientJourneyProgressBar] No current user found');
      toast.error('Unable to update progress: User not authenticated');
      return;
    }

    setIsUpdating(true);
    
    try {
      // Calculate the global step index
      const globalStepIndex = validSections
        .slice(0, selectedSection)
        .reduce((acc, section) => acc + section.substeps.length, 0) + selectedStep;

      // Find existing step if any
      const existingStepIndex = currentJourney.completedSteps.findIndex(
        step => step.stepIndex === selectedStep && step.sectionIndex === selectedSection
      );

      // Prepare the progress data
      const progressData = {
        currentSectionIndex: selectedSection,
        currentStepIndex: selectedStep,
        completedSteps: isStepComplete 
          ? existingStepIndex === -1
            ? [...currentJourney.completedSteps, {
                stepIndex: selectedStep,
                sectionIndex: selectedSection,
                globalStepIndex,
                completedAt: new Date(),
                completedBy: selectedClinician?._id || user._id,
                notes: stepNotes || '',
                status: 'completed',
                lastUpdatedAt: new Date(),
                lastUpdatedBy: user._id,
                history: []
              }]
            : currentJourney.completedSteps.map((step, index) => 
                index === existingStepIndex
                  ? {
                      ...step,
                      notes: stepNotes || '',
                      status: 'completed',
                      lastUpdatedAt: new Date(),
                      lastUpdatedBy: user._id,
                      history: [
                        ...(step.history || []),
                        {
                          status: 'completed',
                          changedAt: new Date(),
                          changedBy: user._id,
                          notes: stepNotes || ''
                        }
                      ]
                    }
                  : step
              )
          : currentJourney.completedSteps.filter(step => 
              !(step.stepIndex === selectedStep && step.sectionIndex === selectedSection)
            )
      };

      // Dispatch the update
      const result = await dispatch(updateClientJourneyProgress({
        clientId,
        progressData
      })).unwrap();
      
      // Call the parent's onUpdateProgress callback with the full result
      onUpdateProgress(result);
      
      // Close the modal and reset state
      setIsModalOpen(false);
      setSelectedClinician(null);
      setStepNotes('');
      setIsStepComplete(false);
      
      toast.success('Progress updated successfully');
    } catch (error) {
      console.error('[ClientJourneyProgressBar] Error updating progress:', error);
      toast.error(error.message || 'Failed to update progress');
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to get completion details for a step
  const getStepCompletionDetails = (stepIndex, sectionIndex) => {
    const step = currentJourney.completedSteps.find(
      step => step.stepIndex === stepIndex && step.sectionIndex === sectionIndex
    );
    if (!step) return null;

    return {
      completedAt: new Date(step.completedAt).toLocaleString(),
      completedBy: step.completedBy,
      lastUpdatedAt: new Date(step.lastUpdatedAt).toLocaleString(),
      lastUpdatedBy: step.lastUpdatedBy,
      notes: step.notes,
      history: step.history || []
    };
  };

  const handleToggleComplete = () => {
    setIsStepComplete(!isStepComplete);
  };

  const handleClinicianSelection = (clinician) => {
    setSelectedClinician(clinician);
  };

  // Calculate dynamic height based on content
  const calculateHeight = () => {
    const sectionLabelsHeight = 20; // Section labels at top
    const progressBarHeight = markerSize; // 20px for the markers
    const stepTextHeight = 24; // Approximate height for 2 lines of step text
    const loopIndicatorHeight = showLoop ? 16 : 0; // Loop indicator if present
    const bufferSpace = 16; // Small buffer
    
    return sectionLabelsHeight + progressBarHeight + stepTextHeight + loopIndicatorHeight + bufferSpace;
  };

  return (
    <>
      <div 
        className="w-full mb-6 px-6" 
        style={{ minHeight: `${calculateHeight()}px` }}
      >
        {/* Section labels */}
        <div className="flex justify-between mb-2">
          {validSections.map((section, i) => (
            <div key={section.name} style={{
              flex: section.substeps.length,
              textAlign: 'center',
              color: section.color,
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: 0.5,
            }}>{section.name}</div>
          ))}
        </div>
        {/* Loop indicator */}
        {showLoop && (
          <div style={{
            position: 'absolute',
            left: `calc(${therapyBlockIdx/(allSteps.length-1)*100}% - 32px)`,
            top: 32,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            fontWeight: 600,
            color: '#4f46e5',
            fontSize: 13,
            background: '#eef2ff',
            borderRadius: 6,
            padding: '1px 8px',
            boxShadow: '0 1px 3px rgba(79, 70, 229, 0.1)',
          }}>
            {loopIcon} Block {currentJourney.loopCount}
          </div>
        )}
        {/* Progress bar */}
        <div className="flex items-center relative" style={{ height: markerSize }}>
          {/* Colored segments */}
          {validSections.map((section, i) => (
            <div key={section.name + '-bar'} style={{
              flex: section.substeps.length,
              height: barHeight,
              background: section.color,
              borderTopLeftRadius: i === 0 ? barHeight/2 : 0,
              borderBottomLeftRadius: i === 0 ? barHeight/2 : 0,
              borderTopRightRadius: i === validSections.length-1 ? barHeight/2 : 0,
              borderBottomRightRadius: i === validSections.length-1 ? barHeight/2 : 0,
              opacity: 0.12,
            }} />
          ))}
          {/* Progress tracker line */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: (markerSize-barHeight)/2,
            height: barHeight,
            width: `${progressPercentage}%`,
            background: `linear-gradient(90deg, ${validSections.map((section, index) => {
              const percentage = (validSections.slice(0, index + 1).reduce((acc, s) => acc + s.substeps.length, 0) / allSteps.length) * 100;
              return `${section.color} ${percentage}%`;
            }).join(', ')})`,
            borderRadius: barHeight/2,
            zIndex: 2,
            transition: 'width 0.3s ease-out',
          }} />
          {/* Step markers */}
          {allSteps.map((step, idx) => {
            if (!step || !step.name) return null;
            
            // Find section color and index
            let sectionIndex = 0;
            let stepIndex = 0;
            let currentCount = 0;

            for (let i = 0; i < validSections.length; i++) {
              const section = validSections[i];
              if (currentCount + section.substeps.length > idx) {
                sectionIndex = i;
                stepIndex = idx - currentCount;
                break;
              }
              currentCount += section.substeps.length;
            }

            const section = validSections[sectionIndex];
            if (!section) return null;

            const isActive = idx === stepIdx;
            const isComplete = isStepCompleted(stepIndex, sectionIndex);
            const isRequired = step.isRequired;
            const isRequiredAndIncomplete = isRequired && !isComplete;
            const completionDetails = getStepCompletionDetails(stepIndex, sectionIndex);

            return (
              <div 
                key={step.name} 
                style={{
                  position: 'absolute',
                  left: `calc(${idx/(allSteps.length-1)*100}% - ${markerSize/2}px)`,
                  top: 0,
                  width: markerSize,
                  height: markerSize,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  zIndex: 3,
                  cursor: 'pointer',
                }}
                onClick={() => handleStepClick(idx, step)}
                onMouseEnter={() => setHoveredStep(idx)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                <div style={{
                  width: markerSize,
                  height: markerSize,
                  borderRadius: '50%',
                  background: isActive ? section.color : isComplete ? section.color : '#f3f4f6',
                  border: isActive ? `2px solid ${section.color}` : '1px solid #fff',
                  boxShadow: isActive ? `0 0 0 3px ${section.color}22` : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isActive ? '#fff' : isComplete ? '#fff' : '#6b7280',
                  fontWeight: 600,
                  fontSize: 12,
                  transition: 'all 0.2s',
                }}>
                  {isComplete ? '✓' : isRequiredAndIncomplete ? redXIcon : idx+1}
                </div>
                <div style={{ 
                  fontSize: 10, 
                  marginTop: 4, 
                  color: isActive ? section.color : isRequiredAndIncomplete ? '#DC2626' : '#6b7280', 
                  fontWeight: isActive || isRequiredAndIncomplete ? 600 : 400, 
                  textAlign: 'center', 
                  maxWidth: 90,
                  minWidth: 60,
                  lineHeight: '1.2',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal'
                }}>
                  {step.name}
                </div>

                {/* Completion Details Tooltip */}
                {hoveredStep === idx && completionDetails && (
                  <div className="absolute bottom-full mb-1 p-2 bg-white rounded-md shadow-sm border border-indigo-100/50 z-50 min-w-[180px]">
                    <div className="text-xs space-y-0.5">
                      <div className="font-medium text-gray-900">Completion Details</div>
                      <div className="text-gray-600 text-[11px]">
                        <div>Completed: {completionDetails.completedAt}</div>
                        <div>By: {completionDetails.completedBy?.firstName} {completionDetails.completedBy?.lastName}</div>
                        <div className="mt-0.5">
                          <span className="font-medium">Notes: </span>
                          {completionDetails.notes || 'N/A'}
                        </div>
                        {completionDetails.history.length > 0 && (
                          <div className="mt-0.5">
                            <div className="font-medium">History:</div>
                            {completionDetails.history.map((entry, i) => (
                              <div key={i} className="text-gray-500">
                                {new Date(entry.changedAt).toLocaleString()} - {entry.notes || 'N/A'}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Journey Progress Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-md bg-white p-4 text-left align-middle shadow-md transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-medium text-gray-900 mb-3"
                  >
                    Step Details
                  </Dialog.Title>

                  <div className="space-y-3">
                    {selectedSection !== null && selectedStep !== null && (
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-xs font-medium text-gray-700 mb-0.5">Section</h4>
                          <p className="text-xs text-gray-900">{validSections[selectedSection]?.name}</p>
                        </div>

                        <div>
                          <h4 className="text-xs font-medium text-gray-700 mb-0.5">Step</h4>
                          <p className="text-xs text-gray-900">{validSections[selectedSection]?.substeps[selectedStep]?.name}</p>
                        </div>

                        <div>
                          <h4 className="text-xs font-medium text-gray-700 mb-0.5">Description</h4>
                          <p className="text-xs text-gray-600">
                            {validSections[selectedSection]?.substeps[selectedStep]?.description || 'No description available'}
                          </p>
                        </div>

                        <div className="flex items-center space-x-4">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-gray-200 text-indigo-600 focus:ring-indigo-500/20"
                              checked={validSections[selectedSection]?.substeps[selectedStep]?.isRequired}
                              readOnly
                            />
                            <span className="ml-1.5 text-xs text-gray-600">Required Step</span>
                          </label>

                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-gray-200 text-indigo-600 focus:ring-indigo-500/20"
                              checked={isStepComplete}
                              onChange={handleToggleComplete}
                            />
                            <span className="ml-1.5 text-xs text-gray-600">Mark as Complete</span>
                          </label>
                        </div>

                        {isStepComplete && (
                          <>
                            <div>
                              <h4 className="text-xs font-medium text-gray-700 mb-0.5">Completed By</h4>
                              {selectedClinician ? (
                                <div className="flex items-center justify-between p-1.5 bg-gray-50 rounded-sm mb-1.5">
                                  <div>
                                    <span className="text-xs font-medium text-gray-900">
                                      {selectedClinician.firstName} {selectedClinician.lastName}
                                    </span>
                                    {selectedClinician.qualifications && (
                                      <span className="text-xs text-gray-500 ml-1.5">
                                        ({selectedClinician.qualifications})
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedClinician(null)}
                                    className="text-gray-400 hover:text-gray-500"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <SearchDropdown
                                  prefix=":clinician:"
                                  displayFields={["firstName", "lastName", "qualifications"]}
                                  onSelection={handleClinicianSelection}
                                  placeholder="Select clinician who completed this step"
                                />
                              )}
                            </div>

                            <div>
                              <h4 className="text-xs font-medium text-gray-700 mb-0.5">Notes</h4>
                              <textarea
                                className="mt-0.5 block w-full rounded-sm border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500/20 text-xs"
                                rows={3}
                                value={stepNotes}
                                onChange={(e) => setStepNotes(e.target.value)}
                                placeholder="Add any notes about this step completion..."
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-sm border border-transparent bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
                      onClick={() => setIsModalOpen(false)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-sm border border-transparent bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 transition-colors disabled:opacity-50 shadow-sm"
                      onClick={handleUpdateProgress}
                      disabled={isUpdating}
                    >
                      {isUpdating ? 'Updating...' : 'Update Progress'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ClientJourneyProgressBar;